<template>
  <slot :hotkey="hotkeyString" />
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted } from 'vue';
import { AnalyticsAPI } from '../../legacy/api/analytics';

type Modifiers = Partial<Pick<KeyboardEvent, 'altKey' | 'metaKey' | 'shiftKey' | 'ctrlKey'>>;

const {
  apple,
  other,
  preventDefault = true,
  onAction = undefined
} = defineProps<{
  apple: {
    mods: Modifiers;
    key: string;
  };
  other: {
    mods: Modifiers;
    key: string;
  };
  preventDefault?: boolean;
  onAction?: () => void;
}>();

const emit = defineEmits<{
  action: [void];
}>();

const platform = ((navigator as any).userAgentData?.platform || navigator.platform).toLowerCase();
const isMac = platform.includes('mac');

const hotkeyConfig = isMac ? apple : other;

const hotkeyString = stringifyHotkey(hotkeyConfig.mods, hotkeyConfig.key);

function isModifiersMatch(e: KeyboardEvent, mods: Modifiers) {
  return (
    e.metaKey === (mods.metaKey ?? false)
    && e.shiftKey === (mods.shiftKey ?? false)
    && e.altKey === (mods.altKey ?? false)
    && e.ctrlKey === (mods.ctrlKey ?? false)
  );
}

function stringifyHotkey(mods: Modifiers, key: string) {
  const parts: string[] = [];

  if (mods.metaKey) {
    parts.push(isMac ? 'Cmd' : 'Win');
  }
  if (mods.ctrlKey) {
    parts.push('Ctrl');
  }
  if (mods.altKey) {
    parts.push('Alt');
  }
  if (mods.shiftKey) {
    parts.push('Shift');
  }
  parts.push(key);

  const thinsp = ' ';
  return parts.join(`${thinsp}+${thinsp}`);
}

function handleKeydown(e: KeyboardEvent) {
  if (e.key?.toLowerCase() === hotkeyConfig.key.toLowerCase() && isModifiersMatch(e, hotkeyConfig.mods)) {
    if (preventDefault) {
      e.preventDefault();
    }
    emit('action');
    AnalyticsAPI.send('hotkey', { hotkey: hotkeyString });
  }
}

onMounted(() => {
  if (onAction) {
    window.addEventListener('keydown', handleKeydown);
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeydown);
});
</script>

<i18n lang="json">{}</i18n>
